import EventEmitter from 'events';

import { isCognitoSessionActive } from '@/auth/utils/cognitoSession';
import { isMfaGateClose, routeToMfaSetup } from '@/auth/utils/mfaGate';
import { fibonacci } from '@/utils';

export const QUEUE_IDS = {
  CONSULTATION_EXPERT_CALL_STATUS: 'consultation:expert_call_status',
  CHAT_UNREAD_MESSAGES: 'chat:unread_messages',
  // The generic conversation domain (CV-5, ENG-5498): the api retired the
  // legacy `engagement` WS service, and the owning domain now lives in the
  // RESOURCE (`<contextType>:<contextId>:<channelType>`). Feature code
  // reaches these two only through
  // `features/consulting-engagements/live/ws.ts` — never by importing
  // QUEUE_IDS directly.
  CONVERSATION_NEW_MESSAGE: 'conversation:new_message',
  CONVERSATION_UNREAD_MESSAGES: 'conversation:unread_messages',
} as const;

export type QueueId = (typeof QUEUE_IDS)[keyof typeof QUEUE_IDS];

export interface QueueKey {
  queue: QueueId;
  resource: string;
}

interface Event {
  service: string;
  queue: string;
  resource: string;
  payload: Record<string, any>;
}

interface SubscribeRequest {
  service: string;
  queue: string;
  resource: string;
  action: 'subscribe' | 'unsubscribe';
}

type Listener = (payload: Record<string, any>) => void;

function buildKey(service: string, queue: string, resource: string) {
  return `${service}:${queue}:${resource}`;
}

// ENG-5251 (PR #2688 review): module-level set of WebSocket instances we
// intentionally closed (token swap, teardown). The onclose handler reads
// from this set synchronously to decide whether to skip the
// fibonacci-backoff reconnect. WeakSet so closed sockets are garbage
// collected without leaks; safe across multiple ApiWebSocket instances
// because the key is the WebSocket object identity.
const intentionalCloses = new WeakSet<WebSocket>();

type TokenGetter = () => string | undefined;

class ApiWebSocket {
  private getApiToken: TokenGetter;
  private lastToken?: string;
  private conn: Promise<WebSocket> | null;
  // ENG-5251 (PR #2688 review): synchronous handle to the live WebSocket.
  // `conn` is a Promise that only resolves on open; `currentWs` is set the
  // moment `new WebSocket(...)` returns, so the token-swap branch can mark
  // the stale socket as `intentionalCloses.add(currentWs)` BEFORE any
  // async boundary, and the onclose handler can identity-guard against
  // clobbering a newer socket via `this.currentWs !== ws`.
  private currentWs: WebSocket | null;
  private emitter: EventEmitter;
  private reconnectAttempts: number;
  private url: string;
  // ENG-5251 (PR #2688 review): registry of live subscriptions keyed by
  // `service:queue:resource`. Populated on first listener for a key,
  // removed on last. Replayed verbatim in `ws.onopen` (token-swap AND
  // fibonacci reconnect both go through `loadConnection`, so both paths
  // resubscribe correctly) so server-side subscriptions are reinstated
  // without forcing consumers to re-call `on()`.
  private activeSubscriptions: Map<string, SubscribeRequest>;

  // ENG-5251: getApiToken is a lazy accessor (typically reads from the
  // redux runtime slice) so mid-session signup/login/logout reaches the
  // socket without re-bootstrapping the app. Previously the token was
  // captured at boot; an anonymous-bootstrap user who signed up mid-session
  // got their first WebSocket opened with no auth subprotocol → server
  // rejected it → live notifications were dead until hard refresh.
  //
  // Token getter is also accepted as `string | undefined` for backwards
  // compatibility with callers passing a frozen token (e.g. tests).
  constructor(url: string, apiToken?: TokenGetter | string) {
    this.emitter = new EventEmitter();
    this.url = url;
    this.getApiToken = typeof apiToken === 'function' ? apiToken : () => apiToken;
    this.reconnectAttempts = 0;
    this.conn = null;
    this.currentWs = null;
    this.activeSubscriptions = new Map();
  }

  // -- EVENT EMITTER

  on(queueId: QueueId, resource: string, listener: Listener) {
    const [service, queue] = queueId.split(':');
    const key = buildKey(service, queue, resource);
    // Send the subscribe frame only on the FIRST listener for this key.
    // Subsequent listeners share the server-side subscription; the registry
    // ensures replay still covers them after a socket swap.
    const isFirstListener = this.emitter.listenerCount(key) === 0;
    this.emitter.on(key, listener);
    if (isFirstListener) {
      const request: SubscribeRequest = { service, queue, resource, action: 'subscribe' };
      this.activeSubscriptions.set(key, request);
      void this.sendRequest(request);
    }
    return () => this.removeListener(queueId, resource, listener);
  }

  removeListener(queueId: QueueId, resource: string, listener: Listener) {
    const [service, queue] = queueId.split(':');
    const key = buildKey(service, queue, resource);
    this.emitter.removeListener(key, listener);
    // Send the unsubscribe frame only when the last listener for this key
    // is gone — siblings still need the subscription.
    if (this.emitter.listenerCount(key) === 0) {
      this.activeSubscriptions.delete(key);
      void this.sendRequest({ service, queue, resource, action: 'unsubscribe' });
    }
  }

  /**
   * ENG-5251 (PR #2688 review SEC-1): tear down all live subscription
   * state on identity change (logout / loginAs). Called from
   * `authSession.clearToken()`, the identity-teardown chokepoint.
   *
   * Without this, the `activeSubscriptions` registry (added for H2 replay)
   * survives a logout. Combined with an in-page identity switch (loginAs),
   * the next authenticated socket would replay the PRIOR identity's
   * subscribe frames (`chat:unread_messages`, `consultation:expert_call_status`)
   * on the NEW identity's socket — a cross-identity subscription leak.
   *
   * Reuses the H1 intentional-close path (`intentionalCloses.add`) so the
   * closed socket's onclose handler skips the fibonacci reconnect. Does NOT
   * clear the EventEmitter listeners — those are managed by each consumer's
   * effect lifecycle (keyed on viewer.id), which re-subscribes the new
   * identity and repopulates the registry on its own.
   */
  reset(): void {
    this.activeSubscriptions.clear();
    const stale = this.currentWs;
    if (stale) {
      intentionalCloses.add(stale);
      try {
        stale.close();
      } catch {
        // Best-effort close — the socket may already be CLOSING/CLOSED.
      }
    }
    this.conn = null;
    this.currentWs = null;
  }

  /**
   * ENG-6102: reconnect now if the token changed since the open socket was
   * created. The api authenticates the handshake only, so a Cognito ID token
   * refreshed every hour would otherwise reach the socket only on its next
   * subscribe frame or unclean close. Goes through loadConnection, so the
   * swap and the subscription replay are the same as on any token change.
   * No socket yet means nothing to swap: the next request opens one.
   */
  refreshToken(): void {
    if (!this.conn || this.getApiToken() === this.lastToken) return;
    void this.loadConnection();
  }

  // -- HANDLE CONNECTION

  private async sendRequest(eventPayload: SubscribeRequest) {
    const ws = await this.loadConnection();
    ws.send(JSON.stringify(eventPayload));
  }

  private loadConnection(): Promise<WebSocket> {
    // Read the token lazily and reconnect if it has changed since the open
    // socket was created (covers mid-session signup/login/logout).
    const currentToken = this.getApiToken();
    if (this.conn && currentToken !== this.lastToken) {
      // Tag the stale socket as an intentional close BEFORE any async
      // boundary — the onclose handler reads `intentionalCloses` and
      // `this.currentWs` synchronously to decide whether to schedule a
      // reconnect and whether to null `this.conn`. Without this synchronous
      // tagging, an onclose firing before the stale socket opened (auth
      // failure / transient network) would null `this.conn` AFTER we've
      // installed the replacement, clobbering it.
      const stale = this.currentWs;
      if (stale) {
        intentionalCloses.add(stale);
        try {
          stale.close();
        } catch {
          // Best-effort close — the socket may already be CLOSING.
        }
      }
      this.conn = null;
      this.currentWs = null;
    }
    if (this.conn) return this.conn;

    this.lastToken = currentToken;
    const ws = new WebSocket(
      this.url,
      currentToken ? ['X-Authorization', currentToken] : undefined
    );
    this.currentWs = ws;
    this.conn = new Promise<WebSocket>((resolve) => {
      ws.onclose = (evt) => {
        console.debug(
          'WebSocket connection closed -',
          'code:',
          evt.code,
          'clean',
          evt.wasClean,
          'reason',
          evt.reason
        );
        if (intentionalCloses.has(ws)) {
          // Token-change swap already nulled this.conn / this.currentWs and
          // may have installed the replacement socket — don't touch state,
          // don't schedule a reconnect.
          return;
        }
        if (this.currentWs !== ws) {
          // A newer socket has already been installed (race: this onclose
          // fired after a swap completed). Do not clobber the newer
          // reference and do not schedule a reconnect — the new socket is
          // already taking over.
          return;
        }
        this.conn = null;
        this.currentWs = null;
        // ENG-6103: the api's MFA gate refused this user. Retrying cannot
        // succeed until they enrol; the page reload after enrolment reconnects.
        if (isMfaGateClose(evt)) {
          routeToMfaSetup();
          return;
        }
        // ENG-6102: in a Cognito session, a clean close we did not ask for
        // (intentional closes returned above), e.g. the server refusing a
        // socket opened by refreshToken() at handshake, would otherwise
        // leave live subscriptions with no socket until the next subscribe
        // call. Legacy sessions keep the old rule: a clean close is final,
        // or a server that accepts then closes would be redialled forever.
        if (!evt.wasClean || (this.activeSubscriptions.size > 0 && isCognitoSessionActive())) {
          // if wasn't a clean close, or a Cognito session is still listening, reconnect
          const seconds = fibonacci(1, ++this.reconnectAttempts);
          console.debug('Attempting to reconnect in', seconds, 'seconds...');
          setTimeout(() => this.loadConnection(), seconds * 1000);
        }
      };

      ws.onmessage = (evt) => {
        const data = JSON.parse(evt.data) as Event;
        const key = buildKey(data.service, data.queue, data.resource);
        this.emitter.emit(key, data.payload);
      };

      ws.onopen = () => {
        this.reconnectAttempts = 0;
        // Replay live subscriptions BEFORE resolving the opened promise so
        // any caller awaiting `loadConnection()` sees a socket already in
        // the subscribed state. Covers both the token-swap path (server
        // dropped the prior subscriptions when we closed the stale socket)
        // and the fibonacci-reconnect path (server dropped them on
        // unclean disconnect).
        for (const request of this.activeSubscriptions.values()) {
          try {
            ws.send(JSON.stringify(request));
          } catch (err) {
            // Best-effort per-frame; one bad frame should not block the
            // rest of the replay.
            console.debug('Subscription replay frame failed:', err);
          }
        }
        resolve(ws);
      };
    });

    return this.conn;
  }
}

export default ApiWebSocket;
